import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PieChart } from '@mui/x-charts/PieChart';
import images from '../../../Utils/images';
import CheerioButton from '../../../Components/CheerioButton';
import colors from '../../../Utils/colors';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import SuccessModal from './SuccessModal';
import '../../../../src/Pages/Templates/styles.css';
import { PrimeIcons } from 'primereact/api';
import { getWalletHistory } from '../../../Services';
import { eventsend } from '../../../Config/analyticsFunctions';

const HistoryModal = ({ setIsHistoryModalOpen, isHistoryModalOpen, onAddBalance, setIsAutomationTaskModalOpen, isAutomationTaskModalOpen }) => {
  const authtoken = useSelector((state) => state.main.auth_token);
  const localdata = useSelector((state) => state.main.localdata);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const handleClose = () => {
    setIsHistoryModalOpen(false);
  };

  const walletCurrency = localdata?.walletCurrency ?? 'USD';
  const [walletHistory, setWalletHistory] = useState([]);
  const [usageData, setUsageData] = useState({
    autotopup: 0,
    manual: 0,
    whatsapp: 0,
    email: 0,
    sms: 0,
    refund: 0,
    verification: 0,
    transfer: 0,
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Mock data - replace with actual data from your state/API
  const walletBalance = localdata?.walletBalance;
  const AutomationPaidQutoaCount = localdata?.PaidQutoaCount * .25 || 0;
  // Chart data for donut chart - now using real data
  const chartData = [
    { id: 0, value: usageData.whatsapp, label: 'WhatsApp Message', color: '#8B5CF6' },
    { id: 1, value: usageData.sms, label: 'SMS Campaign', color: '#EF4444' },
    { id: 2, value: usageData.email, label: 'Email Campaign', color: '#F97316' },
    { id: 3, value: usageData.verification, label: 'Email Verification', color: '#10B981' },
    { id: 4, value: usageData.transfer, label: 'Email Credits', color: '#06B6D4' },
    { id: 5, value: usageData.manual, label: 'Manual Top-up', color: '#6366F1' },
    { id: 6, value: usageData.autotopup, label: 'Auto Top-up', color: '#EC4899' },
    { id: 7, value: usageData.refund, label: 'Refund', color: '#14B8A6' },
    { id: 8, value: AutomationPaidQutoaCount, label: 'Automation Tasks', color: '#F59E0B' }
  ].filter(item => item.value > 0); // Only show categories with actual usage

  const fetchWalletHistory = async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const data = await getWalletHistory(authtoken, { page, limit: 20 });

      if (data?.success) {
        if (append) {
          setWalletHistory(prev => [...prev, ...data.data]);
        } else {
          setWalletHistory(data.data);
        }

        setPagination(data.pagination);
        setUsageData(data.usage || {});
      }
    } catch (error) {
      console.error('Error fetching wallet history:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (isHistoryModalOpen) {
      eventsend('wallet_history_viewed', {
        screen_name: "wallet_history_modal",
        account_id: localdata?._id,
        platform: 'web',
        plan_type: localdata?.premiumPlanName,
        timestamp: new Date().toISOString(),
      });
      fetchWalletHistory(1, false);
    }
  }, [authtoken, isHistoryModalOpen]);

  const loadMoreTransactions = () => {
    if (pagination.hasNext && !loadingMore) {
      fetchWalletHistory(pagination.currentPage + 1, true);
    }
  };

  function formatDateTime(timestamp) {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const formattedTime = `${String(hours).padStart(2, '0')}:${minutes}${ampm}`;
    return `${day}-${month}-${year}, ${formattedTime}`;
  }

  const getTransactionAmount = (txn) => {
    const symbol = txn.currency === 'INR' ? '₹' : '$';
    const sign = txn.type === 'credit' ? '+' : '-';
    return `${sign}${symbol}${Number(txn.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getTransactionColor = (txn) => {
    return txn.type === 'credit' ? '#28a745' : '#dc3545';
  };

  const getTransactionTitle = (txn) => {
    switch (txn.source) {
      case "manual": return "Manual Top up";
      case "autotopup": return "Auto Top up";
      case "campaign": return txn.description || "Campaign";
      case "verification": return "Email Verification";
      case 'transfer': return "Email Credits Transfer";
      case 'whatsapp': return "WhatsApp Messages";
      case 'email': return "Email Campaign";
      case 'sms': return "SMS Campaign";
      case 'refund': return "Refund";
      default: return txn.description || "Transaction";
    }
  };

  // const filteredTransactions = walletHistory;

  return (
    <Dialog
      open={isHistoryModalOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: { xs: 2, md: 4 },
          m: { xs: 1, md: 4 },
          width: '100%',
          maxWidth: '1200px',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, pb: 2 }}>
        <span style={{ fontWeight: 600, fontSize: 18 }}>Cheerio Wallet</span>
        <IconButton onClick={handleClose} size="large">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: fullScreen ? 'column' : 'row',
            height: fullScreen ? 'auto' : '70vh',
            minHeight: 400,
            width: '100%',
          }}
        >
          {/* Left Column (Pie chart and balance) */}
          <div
            style={{
              flex: fullScreen ? 'unset' : '0 0 45%',
              width: fullScreen ? '100%' : '45%',
              padding: 24,
              borderRight: fullScreen ? 'none' : '1px solid #E6E6E6',
              borderBottom: fullScreen ? '1px solid #E6E6E6' : 'none',
              overflowY: 'auto',
            }}
          >
            {/* Current Balance Section */}
            <div style={{ marginBottom: '32px' }}>
              <h6
                style={{
                  fontWeight: 500,
                  fontSize: '14px',
                  color: '#666',
                  marginBottom: '8px',
                }}
              >
                Current Balance
              </h6>
              <h2
                style={{
                  fontWeight: 700,
                  fontSize: '32px',
                  color: '#000',
                  marginBottom: '16px',
                }}
              >
                {walletCurrency === 'INR' ? '₹' : '$'}{walletBalance?.toFixed(3) || '0.000'}
              </h2>
              <button
                onClick={() => {
                  setIsHistoryModalOpen(false);
                  if (onAddBalance) onAddBalance();
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#8B5CF6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '16px',
                  cursor: 'pointer',
                }}
              >
                Add Balance
              </button>
            </div>

            {/* Usage Breakdown Section (Pie Chart) */}
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                }}
              >
                <h6
                  style={{
                    fontWeight: 600,
                    fontSize: '16px',
                    color: '#000',
                    margin: 0,
                  }}
                >
                  Usage Breakdown
                </h6>
                <span
                  style={{
                    fontSize: '14px',
                    color: '#666',
                  }}
                >
                  Last 30 days
                </span>
              </div>

              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                  <CircularProgress size={40} />
                </div>
              ) : chartData.length > 0 ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                    <PieChart
                      series={[
                        {
                          data: chartData,
                          innerRadius: 30,
                          outerRadius: 60,
                        },
                      ]}
                      width={120}
                      height={120}
                      legend={{ hidden: true }}
                      margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                      skipAnimation
                    />
                  </div>
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                      {chartData.map((item) => (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{
                            width: '8px',
                            height: '8px',
                            backgroundColor: item.color,
                            borderRadius: '50%'
                          }} />
                          <span style={{ fontSize: '12px', color: '#666' }}>{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    {chartData.slice(0, 5).map((item, index) => (
                      <div
                        key={item.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px 0',
                          borderBottom: index === 0 ? '1px solid #F0F0F0' : 'none',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '14px', color: '#000' }}>{item.label}</span>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            backgroundColor: '#E6E6E6',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <span style={{ fontSize: '10px', color: '#666' }}>ℹ</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: '14px', fontWeight: 600, color: '#000' }}>
                            ₹{item.value}
                          </span>
                          {item?.label === "Automation Tasks" && (
                            <i
                              className={PrimeIcons.CHEVRON_RIGHT}
                              style={{
                                fontSize: '14px',
                                color: '#666',
                                cursor: item.label === 'Automation Tasks' ? 'pointer' : 'default'
                              }}
                              onClick={() => {
                                if (item.label === 'Automation Tasks') {
                                  setIsAutomationTaskModalOpen(true);
                                  setIsHistoryModalOpen(false);
                                }
                              }}
                            ></i>

                          )}
                        </div>
                      </div>
                    ))}

                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', color: '#666', marginTop: '40px' }}>
                  No usage data available
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Transaction List */}
          <div
            style={{
              flex: fullScreen ? 'unset' : '0 0 55%',
              width: fullScreen ? '100%' : '55%',
              padding: 24,
              overflowY: 'auto',
            }}
          >
            {/* Recent Transactions Section */}
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                }}
              >
                <h6
                  style={{
                    fontWeight: 600,
                    fontSize: '16px',
                    color: '#000',
                    margin: 0,
                  }}
                >
                  Recent Transactions
                </h6>
              </div>

              {/* Transaction List */}
              <div>
                {loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress size={40} />
                  </div>
                ) : walletHistory.length === 0 ? (
                  <div style={{ color: '#888', textAlign: 'center', marginTop: '40px' }}>
                    No transactions found.
                  </div>
                ) : (
                  <>
                    {walletHistory.map((txn, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '18px 0',
                        borderBottom: index < walletHistory.length - 1 ? '1px solid #F0F0F0' : 'none',
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: '15px', color: '#000', marginBottom: 2 }}>
                            {getTransactionTitle(txn)}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: 2 }}>
                            {formatDateTime(txn.date)}
                          </div>
                          {txn.isGrouped && (
                            <div style={{ fontSize: '11px', color: '#888', fontStyle: 'italic' }}>
                              {txn.transactionCount} transactions grouped
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{
                            fontWeight: 700,
                            fontSize: '15px',
                            color: getTransactionColor(txn),
                            marginBottom: 2,
                          }}>
                            {getTransactionAmount(txn)}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {txn.type === 'credit' ? 'Credit' : 'Debit'}
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Load More Button */}
                    {pagination.hasNext && (
                      <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <button
                          onClick={loadMoreTransactions}
                          disabled={loadingMore}
                          style={{
                            padding: '12px 24px',
                            backgroundColor: loadingMore ? '#ccc' : '#8B5CF6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: 600,
                            fontSize: '14px',
                            cursor: loadingMore ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            margin: '0 auto',
                          }}
                        >
                          {loadingMore && <CircularProgress size={16} color="inherit" />}
                          {loadingMore ? 'Loading...' : 'Load More'}
                        </button>
                      </div>
                    )}

                    {/* Pagination Info */}
                    {/* {pagination.totalItems > 0 && (
                      <div style={{
                        textAlign: 'center',
                        marginTop: '16px',
                        fontSize: '12px',
                        color: '#666'
                      }}>
                        Showing {filteredTransactions.length} of {pagination.totalItems} transactions
                      </div>
                    )} */}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HistoryModal;